import React, { useState, useEffect, useRef } from 'react';
import {
  getFirestore,
  query,
  collection,
  where,
  getDocs,
  limit,
  startAfter,
  orderBy,
  getCountFromServer,
} from 'firebase/firestore';
import debounce from 'lodash.debounce';
import CustomLayout from '../../components/layout/Layout';
import ConsultaCidTussFilterPanel from '../../components/consultaCidTuss/ConsultaCidTussFilterPanel';
import ConsultaCidTussResultView from '../../components/consultaCidTuss/result/ConsultaCidTussResultView';
import ConsultaCidTussPagination from '../../components/consultaCidTuss/pagination/ConsultaCidTussPagination';
import ConsultaCidTussDetailsModal from '../../components/consultaCidTuss/modals/ConsultaCidTussDetailsModal';
import { Spin } from 'antd';

const ConsultaCidTuss = () => {
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Default page size
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [lastVisible, setLastVisible] = useState(null);

  const cache = useRef({});
  const itemCache = useRef({});

  const collections = ['Tabela_22_Procedimentos_e_eventos_em_saúde'];

  useEffect(() => {
    fetchTotalResults();
    fetchData();
  }, [filters, currentPage, pageSize]);

  const fetchTotalResults = async () => {
    const db = getFirestore();
    const countQuery = query(collection(db, collections[0]));
    const snapshot = await getCountFromServer(countQuery);
    setTotalResults(snapshot.data().count);
  };

  const fetchData = async () => {
    setLoading(true);

    const cacheKey = `${JSON.stringify(filters)}-${currentPage}-${pageSize}`;
    if (cache.current[cacheKey]) {
      setResults(cache.current[cacheKey].results);
      setLastVisible(cache.current[cacheKey].lastVisible);
      setLoading(false);
      return;
    }

    const db = getFirestore();
    const items = [];

    let q = query(collection(db, 'codigosCID'), orderBy('id'), limit(pageSize));

    if (filters.searchCode) {
      const searchCode = filters.searchCode.toLowerCase();
      q = query(
        collection(db, 'codigosCID'),
        where('id', '>=', searchCode),
        where('id', '<=', searchCode + '\uf8ff'),
        orderBy('id'),
        limit(pageSize)
      );
    }

    if (filters.searchName) {
      const searchName = filters.searchName.toLowerCase();
      q = query(
        collection(db, 'codigosCID'),
        where('name', '>=', searchName),
        where('name', '<=', searchName + '\uf8ff'),
        orderBy('name'),
        limit(pageSize)
      );
    }

    if (lastVisible && currentPage > 1) {
      q = query(q, startAfter(lastVisible));
    }

    const querySnapshot = await getDocs(q);
    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    setLastVisible(lastDoc);

    querySnapshot.forEach((doc) => {
      const data = { id: doc.id, ...doc.data() };
      items.push(data);
    });

    cache.current[cacheKey] = { results: items, lastVisible: lastDoc };
    setResults(items);
    setLoading(false);
  };

  const handleFilterChange = (changedFilters) => {
    setFilters({ ...filters, ...changedFilters });
    setCurrentPage(1);
    setLastVisible(null);
    cache.current = {}; // Clear cache when filters change
  };

  const handleSearch = debounce((value) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...value }));
    setCurrentPage(1);
    setLastVisible(null);
    cache.current = {}; // Clear cache when search changes
  }, 300);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setLastVisible(null);
    cache.current = {}; // Clear cache when page size changes
  };

  const handleItemClick = (item) => {
    if (!itemCache.current[item.id]) {
      itemCache.current[item.id] = item;
    }
    setSelectedItem(itemCache.current[item.id]);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  return (
    <CustomLayout>
      <div className="consulta-cid-tuss-layout bg-gray-50 p-6 rounded-lg shadow-lg">
        <header className="consulta-cid-tuss-header text-center mb-6">
          <h2 className="font-semibold text-gray-800">Consulta TUSS</h2>
        </header>

        <main className="consulta-cid-tuss-content space-y-6">
          <ConsultaCidTussFilterPanel
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />

          {loading ? (
            <div className="flex justify-center items-center">
              <Spin size="large" />
            </div>
          ) : results.length > 0 ? (
            <ConsultaCidTussResultView
              results={results}
              onItemClick={handleItemClick}
              viewMode={viewMode}
            />
          ) : (
            <div className="text-center text-gray-600">
              Nenhum resultado encontrado.
            </div>
          )}

          <ConsultaCidTussPagination
            currentPage={currentPage}
            totalResults={totalResults}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />

          <ConsultaCidTussDetailsModal
            visible={isModalVisible}
            selectedItem={selectedItem}
            onClose={handleModalClose}
          />
        </main>
      </div>
    </CustomLayout>
  );
};

export default ConsultaCidTuss;
